// Assign/convert API client for the AllBrainer LP SDK.
// Fetches variant assignments once per page load and posts conversions silently.

export interface Assignment {
  experimentKey: string;
  variantId: string;
  elementKey: string;
  value: string;
}

export interface FetchAssignmentsOptions {
  baseUrl: string;
  project: string;
  visitor: string;
  page?: string;
}

export interface PostConvertOptions {
  baseUrl: string;
  project: string;
  visitor: string;
  event: string;
  session?: string;
  value?: number;
}

// One in-flight promise per requested URL so multiple callers for the same
// page share one request, while a different page (e.g. an explicit override)
// still triggers its own fetch.
const _pending = new Map<string, Promise<Assignment[]>>();

export function resetAssignmentCache(): void {
  _pending.clear();
}

export function fetchAssignments(opts: FetchAssignmentsOptions): Promise<Assignment[]> {
  const url = new URL(
    `${opts.baseUrl}/api/v1/lp/${encodeURIComponent(opts.project)}/assign`
  );
  url.searchParams.set('visitor', opts.visitor);
  if (opts.page) url.searchParams.set('page', opts.page);
  const key = url.toString();

  const cached = _pending.get(key);
  if (cached) return cached;

  const promise = fetch(key)
    .then((res): Promise<{ assignments: Assignment[] }> => {
      if (!res.ok) return Promise.resolve({ assignments: [] });
      return res.json() as Promise<{ assignments: Assignment[] }>;
    })
    .then((data) => {
      const list = data.assignments;
      return Array.isArray(list) ? list : [];
    })
    .catch(() => []);

  _pending.set(key, promise);
  return promise;
}

export async function postConvert(opts: PostConvertOptions): Promise<void> {
  try {
    await fetch(`${opts.baseUrl}/api/v1/lp/convert`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        project: opts.project,
        visitor: opts.visitor,
        event: opts.event,
        ...(opts.session !== undefined && { session: opts.session }),
        ...(opts.value !== undefined && { value: opts.value }),
      }),
    });
  } catch {
    // Never throw into the host page — failures are intentionally silent.
  }
}
